import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { BASE_URL } from '../helper/constants';
import './PdfPreviewTile.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DEFAULT_MAX_WIDTH = 600;
const DEFAULT_MAX_HEIGHT = 460;
const FILENAME_BAR_HEIGHT = 36;

// Custom properties don't get React's automatic "px" suffix.
const cssSize = (v) => (typeof v === 'number' ? `${v}px` : v);

function resolvePdfUrl(item) {
    if (!item) return '';
    if (item.url && (item.url.startsWith('blob:') || item.url.startsWith('http'))) return item.url;
    if (item.local && item.url) return item.url;
    if (item.href) {
        if (item.href.startsWith('http')) return item.href;
        return BASE_URL + item.href;
    }
    if (item.fileUrl) {
        const furl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
        if (furl && furl.startsWith('http')) return furl;
        if (furl) return BASE_URL + furl;
    }
    return '';
}

function getPdfName(item) {
    if (!item) return 'document.pdf';
    if (item.attachedFileName) return String(item.attachedFileName).split('?')[0];
    if (item.originalName) return String(item.originalName).split('?')[0];
    if (item.fileName && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.fileName)) return String(item.fileName).split('?')[0];
    if (item.name && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.name)) return String(item.name).split('?')[0];
    return 'document.pdf';
}

function computeRenderWidth(nativeW, nativeH, availableW, maxW, maxH) {
    if (!nativeW || !nativeH) return null;
    const aspect = nativeW / nativeH;
    const capW = Math.min(nativeW, maxW, availableW || maxW);
    const capH = maxH - FILENAME_BAR_HEIGHT;

    let w = capW;
    let h = w / aspect;
    if (h > capH) {
        h = capH;
        w = h * aspect;
    }
    return Math.max(1, Math.floor(w));
}

export default function PdfPreviewTile({
    item,
    pdfUrl: overrideUrl,
    fileName: overrideName,
    onClick,
    onRemove,
    showRemove = false,
    isInternal = false,
    variant = 'card',
    maxWidth = DEFAULT_MAX_WIDTH,
    maxHeight = DEFAULT_MAX_HEIGHT,
    width,
    height,
    style = {},
}) {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageDims, setPageDims] = useState(null);
    const [availableWidth, setAvailableWidth] = useState(0);
    const containerRef = useRef(null);

    const isCard = variant === 'card';

    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const measure = () => setAvailableWidth(el.clientWidth);
        measure();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(measure);
            ro.observe(el);
            return () => ro.disconnect();
        }
    }, []);

    const onDocLoad = useCallback(({ numPages: n }) => {
        setNumPages(n);
    }, []);

    const onFirstPageLoad = useCallback((page) => {
        setPageDims({ width: page.originalWidth, height: page.originalHeight });
        setLoaded(true);
    }, []);

    const goToPrev = useCallback((e) => {
        e.stopPropagation();
        setCurrentPage((p) => Math.max(1, p - 1));
    }, []);

    const goToNext = useCallback((e) => {
        e.stopPropagation();
        setCurrentPage((p) => Math.min(numPages || 1, p + 1));
    }, [numPages]);

    const accent = isInternal ? 'var(--kjuup-internal)' : 'var(--kjuup-accent)';
    const name = overrideName || getPdfName(item);

    const rootClassName = [
        'pdf-preview-tile',
        isCard ? 'pdf-preview-tile--card' : 'pdf-preview-tile--plain',
        isInternal ? 'pdf-preview-tile--internal' : '',
        onClick ? 'pdf-preview-tile--clickable' : '',
    ].filter(Boolean).join(' ');

    const fileSource = useMemo(() => {
        const raw = overrideUrl || resolvePdfUrl(item);
        if (!raw) return null;
        return { url: raw };
    }, [overrideUrl, item]);

    const renderWidth = useMemo(() => {
        if (typeof width === 'number') return width;
        if (pageDims) {
            return computeRenderWidth(
                pageDims.width, pageDims.height,
                availableWidth, maxWidth, maxHeight,
            );
        }
        return Math.min(maxWidth, availableWidth || maxWidth);
    }, [pageDims, availableWidth, maxWidth, maxHeight, width]);

    const hasMultiplePages = numPages && numPages > 1;

    return (
        <div
            ref={containerRef}
            onClick={onClick}
            className={rootClassName}
            style={{
                '--pdf-tile-h': cssSize(height || 'auto'),
                '--pdf-tile-max-w': cssSize(maxWidth),
                '--pdf-tile-max-h': cssSize(maxHeight),
                ...style,
            }}
            title={name}
        >
            {showRemove && onRemove && (
                <span
                    className="material-symbols-outlined pointer position-absolute remove-image-icon pdf-preview-tile__remove"
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                >
                    cancel
                </span>
            )}

            {fileSource && !error ? (
                <div className={`pdf-preview-tile__canvas${isCard ? '' : ' pdf-preview-tile__canvas--plain'}`}>
                    <Document
                        file={fileSource}
                        onLoadSuccess={onDocLoad}
                        onLoadError={() => setError(true)}
                        loading={<FallbackIcon accent={accent} name={name} />}
                        noData={<FallbackIcon accent={accent} name={name} />}
                    >
                        <Page
                            key={currentPage}
                            pageNumber={currentPage}
                            width={renderWidth || undefined}
                            onLoadSuccess={currentPage === 1 ? onFirstPageLoad : undefined}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                        />
                    </Document>

                    {hasMultiplePages && (
                        <>
                            <NavButton direction="left" disabled={currentPage <= 1} onClick={goToPrev} accent={accent} isCard={isCard} />
                            <NavButton direction="right" disabled={currentPage >= numPages} onClick={goToNext} accent={accent} isCard={isCard} />
                        </>
                    )}
                </div>
            ) : (
                <FallbackIcon accent={accent} name={name} />
            )}

            {isCard && hasMultiplePages && (
                <div className="pdf-preview-tile__progress">
                    <div
                        className={`pdf-preview-tile__progress-fill${isInternal ? ' pdf-preview-tile__progress-fill--internal' : ''}`}
                        style={{ '--pdf-progress': `${(currentPage / numPages) * 100}%` }}
                    />
                </div>
            )}

            <div
                className={`pdf-preview-tile__footer${isCard ? ' pdf-preview-tile__footer--card' : ''}${!isCard && isInternal ? ' pdf-preview-tile__footer--internal' : ''}`}
            >
                <span className="material-symbols-outlined pdf-preview-tile__file-icon">
                    picture_as_pdf
                </span>
                <span className="pdf-preview-tile__name">
                    {name}
                </span>
                {hasMultiplePages && (
                    <span className="pdf-preview-tile__pages">
                        {currentPage} of {numPages}
                    </span>
                )}
            </div>
        </div>
    );
}

function NavButton({ direction, disabled, onClick, accent, isCard }) {
    const isLeft = direction === 'left';

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`kj-btn kj-btn--icon kj-btn--round kj-btn--on-surface ${isCard ? 'kj-btn--lg' : 'kj-btn--sm'} pdf-preview-tile__nav pdf-preview-tile__nav--${isLeft ? 'left' : 'right'}${isCard ? ' pdf-preview-tile__nav--card' : ''}${disabled ? ' pdf-preview-tile__nav--hidden' : ''}`}
            style={{ '--kj-tone': accent }}
            title={isLeft ? 'Previous page' : 'Next page'}
        >
            <span className={`material-symbols-outlined pdf-preview-tile__nav-icon${isCard ? ' pdf-preview-tile__nav-icon--card' : ''}`}>
                {isLeft ? 'chevron_left' : 'chevron_right'}
            </span>
        </button>
    );
}

function FallbackIcon({ accent }) {
    return (
        <div className="pdf-preview-tile__fallback">
            <span className={`material-symbols-outlined pdf-preview-tile__fallback-icon${accent === 'var(--kjuup-internal)' ? ' pdf-preview-tile__fallback-icon--internal' : ''}`}>
                picture_as_pdf
            </span>
            <span className="pdf-preview-tile__fallback-label">PDF</span>
        </div>
    );
}
